<!-- Tutors: what you pay each per hour for each student, the hours they've taught, their pay lesson by lesson. -->
<script lang="ts">
  import * as A from "../../settings/amounts.ts";
  import { pairingPay, personOverview, tutorSource, tutorSummary, hoursText, type StatementEntry, type Tutor } from "@split/core";
  import { lessonLookup, tutoring } from "../../lib/tutoring.svelte.ts";
  import { app } from "../../lib/store.svelte.ts";
  import { link } from "../../lib/router.svelte.ts";
  import PersonCard from "../../components/PersonCard.svelte";
  import { formatCents, readMoney, shortDateTime, today } from "../../lib/format.ts";
  import { fill, shows } from "../../settings/index.ts";
  import * as T from "../../settings/tutoring.ts";

  let adding = $state(false);
  let editing = $state<Tutor | null>(null);
  let f = $state({ first: "", last: "", contact: "", pay: "", note: "", userId: "", split: "" });

  // People in your Splits groups, to link a tutor's pay to their balance there.
  let splitPeople = $state<{ key: string; groupId: string; personId: string; label: string; name: string }[]>([]);
  $effect(() => {
    void app.groups.length;
    void (async () => {
      const out: typeof splitPeople = [];
      for (const g of app.groups) {
        const st = await app.groupSnapshot(g.id);
        if (!st) continue;
        for (const pid of st.personOrder) {
          const p = st.people[pid];
          if (p && !p.archived && pid !== st.settings.ownerPersonId)
            out.push({ key: `${g.id}|${pid}`, groupId: g.id, personId: pid, name: p.name, label: `${p.name} · ${st.name || g.name}` });
        }
      }
      splitPeople = out;
    })();
  });
  /** The Splits person whose first name matches, to suggest when adding a tutor. */
  const guessSplit = (first: string) =>
    first.trim() ? (splitPeople.find((x) => x.name.split(" ")[0]!.toLowerCase() === first.trim().toLowerCase())?.key ?? "") : "";
  let paying = $state<string | null>(null);
  let pay = $state({ amount: "", on: today(), method: "zelle" });
  let buying = $state<string | null>(null);
  let buy = $state({ what: "", amount: "" });

  const list = $derived(tutoring.tutors.filter((t) => !t.archived).map((t) => ({ t, sum: tutorSummary(t, tutoring.entries) })));
  /** On the site (they run their own calendar), waiting for them to confirm, or you run it. */
  const statusOf = (t: Tutor) =>
    !t.userId
      ? { cls: "", text: T.TUTOR_STATUS_OFF }
      : t.linkConfirmed
        ? { cls: "ok", text: T.TUTOR_STATUS_ON }
        : { cls: "", text: T.TUTOR_STATUS_WAIT };
  /** Each tutor as a person: their tutoring pay is their one source here. */
  const look = $derived(lessonLookup(tutoring.slots, tutoring.students));
  const overviewOf = (t: Tutor) => personOverview([tutorSource(t, tutoring.entries, look)]);
  /** Opened by default, like the pay card was; tap a name to fold it. */
  let folded = $state<Record<string, boolean>>({});
  /** A payout you logged: "Remove" (logged by mistake), with Undo on the pop-up. */
  const payoutAction = (e: StatementEntry) =>
    e.kind === "payout" && shows(T.TUTOR_REMOVE_PAYOUT)
      ? { label: T.TUTOR_REMOVE_PAYOUT, run: () => void tutoring.setPaid(e.id, false) }
      : null;
  /** Their students, each with what you pay them for that student ("Yulin Wang $20.00/hr"). */
  const studentsOf = (id: string) =>
    tutoring.students
      .filter((s) => !s.archived && (s.tutorId === id || tutoring.pairings.some((p) => p.studentId === s.id && p.tutorId === id)))
      .map((s) => `${s.name} ${formatCents(pairingPay(s.id, id, tutoring.pairings))}/hr`)
      .join(", ");
  const owedAll = $derived(list.reduce((s, x) => s + Math.max(0, x.sum.owedCents), 0));

  function startEdit(t: Tutor | null) {
    editing = t;
    adding = true;
    f = t
      ? { first: t.name.split(" ")[0] ?? "", last: t.name.split(" ").slice(1).join(" "), contact: t.contact, pay: "", note: t.note, userId: t.userId ?? "", split: t.splitGroupId && t.splitPersonId ? `${t.splitGroupId}|${t.splitPersonId}` : t.splitPersonId === "" ? "apart" : "" }
      : { first: "", last: "", contact: "", pay: "", note: "", userId: "", split: "" };
  }
  async function save(e: SubmitEvent) {
    e.preventDefault();
    // "" = link by name when there's a clear match; "apart" = you pay them separately.
    const [splitGroupId, splitPersonId] = f.split === "apart" ? ["", ""] : f.split ? f.split.split("|") : [null, null];
    const body = { splitGroupId: splitGroupId ?? null, splitPersonId: splitPersonId ?? null, name: `${f.first.trim()} ${f.last.trim()}`.trim(), contact: f.contact.trim(), note: f.note.trim(), userId: f.userId || null };
    if (editing) await tutoring.update("tutors", editing.id, body, "Saved");
    else await tutoring.add("tutors", body, `Added ${body.name}`);
    adding = false;
  }
  async function payout(t: Tutor) {
    const cents = readMoney(pay.amount);
    if (!cents) return app.toast("Enter the amount", "error");
    await tutoring.add("entries", { kind: "payout", tutorId: t.id, on: pay.on, cents, method: pay.method }, fill(T.TUTOR_LOG_PAID_TOAST, { name: t.name }));
    paying = null;
  }
  async function sell(t: Tutor) {
    const cents = readMoney(buy.amount);
    if (!cents || !buy.what.trim()) return app.toast("Say what it is and the price", "error");
    await tutoring.add("entries", { kind: "item", tutorId: t.id, on: today(), cents, note: buy.what.trim() }, "Added");
    buying = null;
  }
</script>

<div class="bar">
  <span class="muted">{list.length} {list.length === 1 ? "tutor" : "tutors"}{owedAll ? ` · ${A.TOTAL_OWE_LABEL} ${formatCents(owedAll)}` : ""}</span>
  {#if !adding}<button class="btn primary" onclick={() => startEdit(null)}>Add a tutor</button>{/if}
</div>

{#if adding}
  <form class="panel" onsubmit={save}>
    <h2>{editing ? `Edit ${editing.name}` : "Add a tutor"}</h2>
    <div class="grid">
      <label class="field"><span>First name</span><input class="input" bind:value={f.first} autocomplete="off" required onchange={() => !f.split && !editing && (f.split = guessSplit(f.first))} /></label>
      <label class="field"><span>Last name</span><input class="input" bind:value={f.last} autocomplete="off" required /></label>
      <label class="field"><span>Contact</span><input class="input" bind:value={f.contact} /></label>
      <label class="field"
        ><span>Their account (puts them on the site)</span>
        <select class="input" bind:value={f.userId}>
          <option value="">Not linked</option>
          {#each tutoring.accounts as acc (acc.id)}<option value={acc.id}>{acc.name}</option>{/each}
        </select></label
      >
      <label class="field"
        ><span>{T.TUTOR_LINK_LABEL}</span>
        <select class="input" bind:value={f.split}>
          <option value="">{T.TUTOR_LINK_BY_NAME}</option>
          <option value="apart">{T.TUTOR_LINK_APART}</option>
          {#each splitPeople as p (p.key)}<option value={p.key}>{p.label}</option>{/each}
        </select></label
      >
    </div>
    <label class="field"><span>Notes</span><input class="input" bind:value={f.note} placeholder="Subjects, availability" /></label>
    <div class="btns">
      <button class="btn primary" disabled={!f.first.trim() || !f.last.trim()}>Save</button>
      <button type="button" class="btn quiet" onclick={() => (adding = false)}>Cancel</button>
      {#if editing}<button type="button" class="btn quiet" onclick={() => editing && tutoring.update("tutors", editing.id, { archived: true }, "Archived").then(() => (adding = false))}>Archive</button>{/if}
    </div>
  </form>
{/if}

<div class="list" aria-label="Tutors">
  {#each list as { t, sum } (t.id)}
    {@const st = statusOf(t)}
    <PersonCard
      overview={overviewOf(t)}
      name={t.name}
      scope="tutoring"
      viewer={{ side: "owner" }}
      open={!folded[t.id]}
      ontoggle={() => (folded[t.id] = !folded[t.id])}
      about={[
        studentsOf(t.id) || T.TUTOR_NO_STUDENTS,
        fill(T.TUTOR_TAUGHT_TEXT, { hours: hoursText(sum.minutes), count: sum.sessions, sessions: sum.sessions === 1 ? T.TUTOR_SESSION_WORD : T.TUTOR_SESSIONS_WORD }),
      ]}
      entryAction={payoutAction}
    >
      {#snippet badges()}<span class="badge {st.cls}">{st.text}</span>{/snippet}
      {#snippet actions()}
      <div class="acts">
        <!-- Only for money you really paid them (Zelle, cash): their pay is already credited in their overall total. -->
        <button class="btn small" onclick={() => { paying = t.id; pay = { amount: "", on: today(), method: "zelle" }; }}>{fill(T.TUTOR_LOG_PAID_BUTTON, { name: t.name })}</button>
        <button class="btn small" onclick={() => { buying = t.id; buy = { what: "", amount: "" }; }}>{T.TUTOR_BOUGHT_BUTTON}</button>
        <a class="btn small" href="/tutoring/calendar?tutor={t.id}" use:link>{T.TUTOR_SCHEDULE_LINK}</a>
        <button class="btn small quiet" onclick={() => startEdit(t)}>{T.TUTOR_EDIT_BUTTON}</button>
      </div>
      {#if paying === t.id}
        <form class="act" onsubmit={(e) => { e.preventDefault(); void payout(t); }}>
          <label class="field"><span>{T.TUTOR_FORM_AMOUNT}</span><input class="input money" bind:value={pay.amount} inputmode="decimal" /></label>
          <label class="field"><span>{T.TUTOR_FORM_DATE}</span><input class="input" type="date" bind:value={pay.on} /></label>
          <label class="field"
            ><span>{T.TUTOR_FORM_HOW}</span>
            <select class="input" bind:value={pay.method}><option value="zelle">{T.TUTOR_METHOD_ZELLE}</option><option value="cash">{T.TUTOR_METHOD_CASH}</option><option value="other">{T.TUTOR_METHOD_OTHER}</option></select></label
          >
          <button class="btn primary">{T.TUTOR_FORM_SAVE}</button>
          <button type="button" class="btn quiet" onclick={() => (paying = null)}>{T.TUTOR_FORM_CANCEL}</button>
        </form>
      {/if}
      {#if buying === t.id}
        <form class="act" onsubmit={(e) => { e.preventDefault(); void sell(t); }}>
          <label class="field"><span>{T.TUTOR_FORM_WHAT}</span><input class="input" bind:value={buy.what} /></label>
          <label class="field"><span>{T.TUTOR_FORM_PRICE}</span><input class="input money" bind:value={buy.amount} inputmode="decimal" /></label>
          <button class="btn primary">{T.TUTOR_FORM_SAVE}</button>
          <button type="button" class="btn quiet" onclick={() => (buying = null)}>{T.TUTOR_FORM_CANCEL}</button>
        </form>
      {/if}
      {/snippet}
    </PersonCard>
  {:else}
    <div class="empty">{T.TUTORS_EMPTY}</div>
  {/each}
</div>
{#if shows(T.TUTORS_FOOTNOTE)}<p class="muted small">{T.TUTORS_FOOTNOTE}</p>{/if}
{#if tutoring.repairs.length && shows(T.TUTORS_FIXES_TITLE)}
  <section class="fixes" aria-label={T.TUTORS_FIXES_TITLE}>
    <h2>{T.TUTORS_FIXES_TITLE}</h2>
    <ul>
      {#each tutoring.repairs as f (f.id)}<li class="muted small">{fill(T.TUTORS_FIX_LINE, { when: shortDateTime(f.ranAt), what: f.summary })}</li>{/each}
    </ul>
  </section>
{/if}

<style>
  .bar {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
  }

  .bar .btn {
    margin-left: auto;
  }

  .panel {
    display: grid;
    gap: 10px;
    padding: 16px;
    margin-bottom: 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }

  .panel h2 {
    margin: 0;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
  }

  .btns,
  .acts {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .act {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: flex-end;
    padding: 12px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .act .field {
    flex: 0 1 160px;
  }

  .small {
    font-size: var(--t-xs);
  }

  .fixes h2 {
    margin: 16px 0 4px;
    font-size: var(--t-sm);
  }

  .fixes ul {
    margin: 0;
    padding-left: 18px;
  }
</style>
